import { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import AdminLayout from '../components/AdminLayout';
import { fetchDealers, updateDealerAdminFields } from '@/lib/api/profiles';
import useScrollLock from '@/hooks/useScrollLock';

interface Dealer {
  id: string;
  company: string;
  contact: string;
  email: string;
  phone: string;
  country: string;
  approved: boolean;
  portalAccess: boolean;
  orders: number;
  joined: string;
  notes: string;
}

const initialDealers: Dealer[] = [
  { id: 'd1', company: 'Gulf Marine Supply', contact: 'Mike Torres', email: 'mike@gulfmarine.com', phone: '+1 305 555 0101', country: 'USA', approved: true, portalAccess: true, orders: 14, joined: 'Jan 2025', notes: 'Top performer, Florida region' },
  { id: 'd2', company: 'Atlantic Boat Works', contact: 'Sarah Kim', email: 'sarah@atlanticboat.com', phone: '+1 843 555 0202', country: 'USA', approved: true, portalAccess: true, orders: 9, joined: 'Mar 2025', notes: '' },
  { id: 'd3', company: 'Pacific Marine Center', contact: 'Dave Nguyen', email: 'dave@pacificmarine.com', phone: '+1 206 555 0303', country: 'USA', approved: true, portalAccess: true, orders: 7, joined: 'Feb 2025', notes: '' },
  { id: 'd4', company: 'Nordic Marine AB', contact: 'Lars Eriksson', email: 'lars@nordicmarine.se', phone: '+46 8 555 0404', country: 'Sweden', approved: true, portalAccess: true, orders: 11, joined: 'Dec 2024', notes: 'Scandinavia distributor' },
  { id: 'd5', company: 'Great Lakes Marine', contact: 'Lisa Chen', email: 'lisa@greatlakes.com', phone: '+1 312 555 0505', country: 'USA', approved: true, portalAccess: false, orders: 5, joined: 'Apr 2025', notes: 'Portal access pending setup' },
  { id: 'd6', company: 'Coastal Power Systems', contact: 'James White', email: 'james@coastalpower.com', phone: '+1 910 555 0606', country: 'USA', approved: false, portalAccess: false, orders: 0, joined: 'Apr 2026', notes: 'New application' },
  { id: 'd7', company: 'Bayou Marine Depot', contact: 'Rene Fontenot', email: 'rene@bayoumarine.com', phone: '+1 504 555 0707', country: 'USA', approved: false, portalAccess: false, orders: 0, joined: 'Apr 2026', notes: '' },
  { id: 'd8', company: 'Sydney Marine Supplies', contact: 'Sarah Nguyen', email: 'sarah@sydneymarine.com.au', phone: '+61 2 555 0808', country: 'Australia', approved: true, portalAccess: true, orders: 8, joined: 'Nov 2024', notes: 'APAC region' },
  { id: 'd9', company: 'Gulf Yachts LLC', contact: 'James Whitmore', email: 'james@gulfyachts.ae', phone: '+971 4 555 0909', country: 'UAE', approved: true, portalAccess: true, orders: 6, joined: 'Jan 2025', notes: 'Middle East distributor' },
];

type FilterType = 'all' | 'approved' | 'pending';

function DealersContent() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [dealers, setDealers] = useState<Dealer[]>(initialDealers);
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
  const [notesEdit, setNotesEdit] = useState('');
  const [actionFeedback, setActionFeedback] = useState<{ id: string; msg: string } | null>(null);
  const [error, setError] = useState('');
  useScrollLock(Boolean(selectedDealer));

  useEffect(() => {
    const load = async () => {
      try {
        const nextDealers = await fetchDealers();
        setDealers(nextDealers.map((dealer) => ({
          id: dealer.id,
          company: dealer.company,
          contact: dealer.contact,
          email: dealer.email,
          phone: dealer.phone,
          country: dealer.country,
          approved: dealer.approved,
          portalAccess: dealer.portalAccess,
          orders: dealer.orders,
          joined: dealer.joined,
          notes: dealer.notes,
        })));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load dealers.');
      }
    };

    void load();
  }, []);

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const divider = dark ? 'divide-white/5' : 'divide-gray-100';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[#F97316]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]';

  const filtered = dealers.filter((d) => {
    const matchFilter = filter === 'all' ? true : filter === 'approved' ? d.approved : !d.approved;
    const matchSearch = d.company.toLowerCase().includes(search.toLowerCase()) ||
      d.contact.toLowerCase().includes(search.toLowerCase()) ||
      d.email.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const showFeedback = (id: string, msg: string) => {
    setActionFeedback({ id, msg });
    setTimeout(() => setActionFeedback(null), 2000);
  };

  const toggleApproval = async (id: string) => {
    const target = dealers.find((dealer) => dealer.id === id);
    if (!target) return;

    const nextApproved = !target.approved;
    const nextPortalAccess = nextApproved ? target.portalAccess : false;
    setDealers((prev) =>
      prev.map((dealer) =>
        dealer.id === id
          ? { ...dealer, approved: nextApproved, portalAccess: nextPortalAccess }
          : dealer,
      ),
    );
    showFeedback(id, nextApproved ? 'Dealer approved' : 'Approval revoked');

    try {
      await updateDealerAdminFields(id, {
        approved: nextApproved,
        portalAccess: nextPortalAccess,
      });
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update dealer approval.');
    }
  };

  const togglePortalAccess = async (id: string) => {
    const target = dealers.find((dealer) => dealer.id === id);
    if (!target || !target.approved) return;

    const nextPortalAccess = !target.portalAccess;
    setDealers((prev) =>
      prev.map((dealer) =>
        dealer.id === id ? { ...dealer, portalAccess: nextPortalAccess } : dealer,
      ),
    );
    showFeedback(id, nextPortalAccess ? 'Portal access granted' : 'Portal access revoked');

    try {
      await updateDealerAdminFields(id, {
        portalAccess: nextPortalAccess,
      });
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update portal access.');
    }
  };

  const openDetail = (dealer: Dealer) => {
    setSelectedDealer(dealer);
    setNotesEdit(dealer.notes);
  };

  const saveNotes = async () => {
    if (!selectedDealer) return;
    setDealers((prev) => prev.map((d) => d.id === selectedDealer.id ? { ...d, notes: notesEdit } : d));
    setSelectedDealer((prev) => prev ? { ...prev, notes: notesEdit } : null);
    try {
      await updateDealerAdminFields(selectedDealer.id, { notes: notesEdit });
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save dealer notes.');
    }
  };

  const approvedCount = dealers.filter((d) => d.approved).length;
  const pendingCount = dealers.filter((d) => !d.approved).length;
  const portalCount = dealers.filter((d) => d.portalAccess).length;

  return (
    <div className="space-y-5">
      {error && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Dealers', value: dealers.length, icon: 'ri-store-2-line', color: 'text-[#F97316]' },
          { label: 'Approved', value: approvedCount, icon: 'ri-checkbox-circle-line', color: 'text-green-400' },
          { label: 'Portal Access', value: portalCount, icon: 'ri-key-2-line', color: 'text-yellow-400' },
        ].map((s, i) => (
          <div key={i} className={`${card} rounded-lg p-4 flex items-center gap-4`}>
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 shrink-0">
              <i className={`${s.icon} text-xl ${s.color}`}></i>
            </div>
            <div>
              <p className={`text-2xl font-black ${text}`}>{s.value}</p>
              <p className={`text-xs ${muted}`}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {([
            { val: 'all', label: `All (${dealers.length})` },
            { val: 'approved', label: `Approved (${approvedCount})` },
            { val: 'pending', label: `Pending (${pendingCount})` },
          ] as { val: FilterType; label: string }[]).map((f) => (
            <button
              key={f.val}
              onClick={() => setFilter(f.val)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                filter === f.val
                  ? 'bg-[#F97316] text-white'
                  : dark ? 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10' : 'bg-gray-100 text-gray-500 hover:text-gray-900 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className={`ri-search-line text-sm ${muted}`}></i>
          </div>
          <input
            type="text"
            placeholder="Search dealers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`pl-9 pr-4 py-2 text-sm rounded-lg border transition-colors outline-none w-52 ${inputCls}`}
          />
        </div>
      </div>

      {/* Table */}
      <div className={`${card} rounded-lg overflow-hidden`}>
        <div className={`grid grid-cols-12 gap-3 px-5 py-3 text-xs font-bold uppercase tracking-wide ${muted} border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
          <div className="col-span-3">Company</div>
          <div className="col-span-2">Contact</div>
          <div className="col-span-1 text-center">Country</div>
          <div className="col-span-1 text-center">Orders</div>
          <div className="col-span-2 text-center">Approval</div>
          <div className="col-span-2 text-center">Portal Access</div>
          <div className="col-span-1 text-right">Detail</div>
        </div>

        <div className={`divide-y ${divider}`}>
          {filtered.length === 0 ? (
            <div className={`text-center py-12 ${muted}`}>
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                <i className="ri-store-2-line text-3xl"></i>
              </div>
              <p className="text-sm">No dealers found</p>
            </div>
          ) : filtered.map((dealer) => (
            <div key={dealer.id} className={`grid grid-cols-12 gap-3 px-5 py-4 items-center transition-colors ${dark ? 'hover:bg-white/3' : 'hover:bg-gray-50'}`}>
              <div className="col-span-3">
                <p className={`text-sm font-semibold ${text}`}>{dealer.company}</p>
                <p className={`text-xs ${muted}`}>{dealer.joined}</p>
                {dealer.notes && (
                  <p className={`text-xs mt-0.5 italic truncate ${dark ? 'text-white/30' : 'text-gray-400'}`}>{dealer.notes}</p>
                )}
              </div>
              <div className="col-span-2">
                <p className={`text-sm ${text}`}>{dealer.contact}</p>
                <p className={`text-xs ${muted} truncate`}>{dealer.email}</p>
              </div>
              <div className={`col-span-1 text-center text-xs font-medium ${muted}`}>{dealer.country}</div>
              <div className={`col-span-1 text-center text-sm font-bold ${text}`}>{dealer.orders}</div>

              {/* Approval toggle */}
              <div className="col-span-2 flex justify-center">
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={() => void toggleApproval(dealer.id)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-all ${
                      dealer.approved
                        ? 'bg-green-500/20 text-green-400 hover:bg-red-500/20 hover:text-red-400'
                        : 'bg-yellow-500/20 text-yellow-400 hover:bg-green-500/20 hover:text-green-400'
                    }`}
                    title={dealer.approved ? 'Click to revoke approval' : 'Click to approve'}
                  >
                    <i className={`${dealer.approved ? 'ri-checkbox-circle-line' : 'ri-time-line'} mr-1`}></i>
                    {dealer.approved ? 'Approved' : 'Pending'}
                  </button>
                  {actionFeedback?.id === dealer.id && (
                    <span className="text-[10px] text-green-400 font-semibold">{actionFeedback.msg}</span>
                  )}
                </div>
              </div>

              {/* Portal Access toggle */}
              <div className="col-span-2 flex justify-center">
                <button
                  onClick={() => void togglePortalAccess(dealer.id)}
                  disabled={!dealer.approved}
                  title={!dealer.approved ? 'Approve dealer first' : dealer.portalAccess ? 'Revoke portal access' : 'Grant portal access'}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
                    dealer.portalAccess
                      ? 'bg-[#F97316]/20 text-[#F97316] hover:bg-red-500/20 hover:text-red-400'
                      : dark
                      ? 'bg-white/5 text-white/40 hover:bg-[#F97316]/20 hover:text-[#F97316]'
                      : 'bg-gray-100 text-gray-400 hover:bg-[#F97316]/20 hover:text-[#F97316]'
                  }`}
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className={dealer.portalAccess ? 'ri-key-2-fill' : 'ri-key-2-line'}></i>
                  </div>
                  {dealer.portalAccess ? 'Active' : 'No Access'}
                </button>
              </div>

              {/* Detail */}
              <div className="col-span-1 flex justify-end">
                <button
                  onClick={() => openDetail(dealer)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${dark ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}
                  title="View details"
                >
                  <i className="ri-eye-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dealer Detail Modal */}
      {selectedDealer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className={`${dark ? 'bg-[#1C2B3A] border border-white/10' : 'bg-white border border-gray-200'} rounded-xl w-full max-w-md`}>
            {/* Header */}
            <div className={`flex items-center justify-between px-6 py-4 border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
              <div>
                <h2 className={`font-bold text-base ${text}`}>{selectedDealer.company}</h2>
                <p className={`text-xs ${muted}`}>Dealer since {selectedDealer.joined}</p>
              </div>
              <button
                onClick={() => setSelectedDealer(null)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${dark ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              {/* Status badges */}
              <div className="flex gap-2 flex-wrap">
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${selectedDealer.approved ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  <i className={`${selectedDealer.approved ? 'ri-checkbox-circle-line' : 'ri-time-line'} mr-1`}></i>
                  {selectedDealer.approved ? 'Approved' : 'Pending Approval'}
                </span>
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${selectedDealer.portalAccess ? 'bg-[#F97316]/20 text-[#F97316]' : dark ? 'bg-white/5 text-white/30' : 'bg-gray-100 text-gray-400'}`}>
                  <i className={`${selectedDealer.portalAccess ? 'ri-key-2-fill' : 'ri-key-2-line'} mr-1`}></i>
                  {selectedDealer.portalAccess ? 'Portal Active' : 'No Portal Access'}
                </span>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Contact', value: selectedDealer.contact },
                  { label: 'Country', value: selectedDealer.country },
                  { label: 'Email', value: selectedDealer.email },
                  { label: 'Phone', value: selectedDealer.phone },
                  { label: 'Total Orders', value: String(selectedDealer.orders) },
                  { label: 'Member Since', value: selectedDealer.joined },
                ].map((item) => (
                  <div key={item.label}>
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${muted}`}>{item.label}</p>
                    <p className={`text-sm font-medium ${text}`}>{item.value}</p>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div>
                <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Internal Notes</label>
                <textarea
                  rows={2}
                  value={notesEdit}
                  onChange={(e) => setNotesEdit(e.target.value)}
                  placeholder="Add internal notes about this dealer..."
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors resize-none ${
                    dark ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/20 focus:border-[#F97316]' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]'
                  }`}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => { toggleApproval(selectedDealer.id); setSelectedDealer((prev) => prev ? { ...prev, approved: !prev.approved, portalAccess: prev.approved ? false : prev.portalAccess } : null); }}
                  className={`flex-1 py-2.5 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-colors ${
                    selectedDealer.approved
                      ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  }`}
                >
                  <i className={`${selectedDealer.approved ? 'ri-close-circle-line' : 'ri-checkbox-circle-line'} mr-1`}></i>
                  {selectedDealer.approved ? 'Revoke Approval' : 'Approve Dealer'}
                </button>
                <button
                  onClick={() => { togglePortalAccess(selectedDealer.id); setSelectedDealer((prev) => prev ? { ...prev, portalAccess: !prev.portalAccess } : null); }}
                  disabled={!selectedDealer.approved}
                  className={`flex-1 py-2.5 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                    selectedDealer.portalAccess
                      ? dark ? 'bg-white/5 text-white/60 hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-[#F97316]/20 text-[#F97316] hover:bg-[#F97316]/30'
                  }`}
                >
                  <i className={`${selectedDealer.portalAccess ? 'ri-key-2-line' : 'ri-key-2-fill'} mr-1`}></i>
                  {selectedDealer.portalAccess ? 'Revoke Portal' : 'Grant Portal'}
                </button>
              </div>
              <button
                  onClick={() => void saveNotes()}
                className="w-full bg-[#F97316] text-white font-bold py-2.5 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDealersPage() {
  return (
    <AdminLayout title="Dealers">
      <DealersContent />
    </AdminLayout>
  );
}
